
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface ContactFormHandlerProps {
  nursingHomeName: string;
  nursingHomeId?: string;
  county?: string;
  email?: string;
  submitLabel?: string;
  className?: string;
}

export function ContactFormHandler({ 
  nursingHomeName, 
  nursingHomeId, 
  county, 
  email,
  submitLabel = "Send", 
  className 
}: ContactFormHandlerProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.user_metadata?.first_name || '',
    last_name: user?.user_metadata?.last_name || '',
    email: user?.email || '',
    phone: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (data: { 
    first_name: string; 
    last_name: string; 
    email: string;
    phone: string;
    message: string;
  }) => {
    try {
      setIsLoading(true);

      if (!user) {
        navigate(`/auth/login?redirectTo=/nursing-homes/${nursingHomeId}`);
        
        toast({
          title: "Complete the form to send a message",
          description: `You'll need to create an account to contact ${nursingHomeName}.`
        });
        return;
      }
      
      // Create a lead record
      const { data: leadData, error: leadError } = await supabase.from('nursing_home_leads').insert({
        user_id: user.id,
        nursing_home_id: nursingHomeId,
        nursing_home_name: nursingHomeName,
        location: county,
        status: 'inquiry',
        notes: `Inquiry from contact form: ${data.message}`
      }).select();
      
      if (leadError) throw leadError;
      
      // Construct the claim link for the nursing home
      const claimLink = `${window.location.origin}/claim/${nursingHomeId || ''}`;
      
      // Send anonymized lead notification to nursing home
      try {
        await supabase.functions.invoke('send-transactional-email', {
          body: JSON.stringify({
            type: 'nursing_home_inquiry',
            data: {
              nursingHomeName: nursingHomeName,
              county: county,
              message: `A family has sent an inquiry through Grandpal:\n\n${data.message}\n\nContact them to discuss their needs. Claim your profile to access their contact details.`,
              email: email || "hello@updates.grandpal.co",
              claimLink: claimLink,
              leadId: leadData && leadData[0] ? leadData[0].id : undefined
            }
          })
        });
      } catch (emailError) {
        console.error("Failed to send nursing home inquiry email:", emailError);
        // Don't show an error toast since the main action succeeded
      }

      toast({
        title: "Message sent successfully!",
        description: `Your inquiry has been sent to ${nursingHomeName}. They'll be in touch soon.`,
      });
      
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        variant: "destructive",
        title: "Failed to send message",
        description: error.message || "Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleFormSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="grid gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              name="first_name"
              placeholder="John"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              name="last_name"
              placeholder="Doe"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="john.doe@example.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="087 123 4567"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            name="message"
            placeholder="I am interested in learning more about..."
            rows={4}
            value={formData.message}
            onChange={handleChange}
            required
          />
        </div>
        <Button disabled={isLoading} type="submit">
          {isLoading ? "Submitting..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
